// components/Loadable.tsx
import React, { Suspense, ComponentType, lazy, LazyExoticComponent } from 'react';
import Loader from './Loader';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

function isRetryableError(err: Error): boolean {
  const msg = err?.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    err.name === 'ChunkLoadError' ||
    msg.includes('net::ERR_NETWORK_IO_SUSPENDED') ||
    msg.includes('net::ERR_') ||
    msg.includes('Failed to fetch')
  );
}

async function retryUntilSuccess<T>(factory: () => Promise<T>, retries = 0): Promise<T> {
  try {
    return await factory();
  } catch (err: unknown) {
    if (retries < MAX_RETRIES && isRetryableError(err as Error)) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (retries + 1)));
      return retryUntilSuccess(factory, retries + 1);
    }
    throw err;
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(() =>
    retryUntilSuccess(factory).catch((err: Error) => {
      const isChunkError =
        err?.message?.includes('Failed to fetch dynamically imported module') ||
        err?.message?.includes('Importing a module script failed') ||
        err.name === 'ChunkLoadError' ||
        err?.message?.includes('net::ERR_NETWORK_IO_SUSPENDED');

      if (isChunkError) {
        const key = 'chunk_reload_once';
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw err;
    })
  );
}

const Loadable = <P extends object>(Component: ComponentType<P>): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<Loader />}>
      <Component {...props} />
    </Suspense>
  );
  return WrappedComponent;
};

export default Loadable;